import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import {
  Users, Plus, X, Phone, PhoneCall, Shield, Copy, Edit2,
  UserCheck, UserX, Key, RotateCcw
} from 'lucide-react';

export default function Agents() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [generatedPw, setGeneratedPw] = useState('');
  const [resetPw, setResetPw] = useState({ userId: null, newPassword: '' });
  const [formData, setFormData] = useState({
    username: '', password: '', name: '', email: '', phone: '', role: 'executive'
  });

  useEffect(() => { loadUsers(); }, [showDeleted]);

  const loadUsers = async () => {
    try {
      const res = await api.get(`/api/auth/users?include_deleted=${showDeleted}`);
      setUsers(res.data.users.filter(u => u.username !== 'admin'));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/api/auth/register', formData);
      setGeneratedPw(res.data.generated_password);
      setFormData({ username: '', password: '', name: '', email: '', phone: '', role: 'executive' });
      loadUsers();
    } catch (err) { alert(err.response?.data?.detail || 'Error creating user'); }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    try { await api.post(`/api/auth/users/${id}/deactivate`); loadUsers(); } catch (err) { alert('Error'); }
  };

  const handleActivate = async (id) => {
    try { await api.post(`/api/auth/users/${id}/activate`); loadUsers(); } catch (err) { alert('Error'); }
  };

  const handleSoftDelete = async (id) => {
    if (!window.confirm('Archive this user? They will be unable to login.')) return;
    try { await api.delete(`/api/auth/users/${id}`); loadUsers(); } catch (err) { alert('Error'); }
  };

  const handleRestore = async (id) => {
    try { await api.post(`/api/auth/users/${id}/restore`); loadUsers(); } catch (err) { alert('Error'); }
  };

  const handleResetPassword = async () => {
    if (!resetPw.userId || !resetPw.newPassword) return;
    try {
      await api.post(`/api/auth/users/${resetPw.userId}/reset-password`, { new_password: resetPw.newPassword });
      alert('Password reset successfully');
      setResetPw({ userId: null, newPassword: '' });
    } catch (err) { alert('Error resetting password'); }
  };

  const [editUser, setEditUser] = useState({});

  const handleUserUpdate = async (userId) => {
    const data = editUser[userId];
    if (!data) return;
    try {
      await api.put(`/api/auth/users/${userId}`, data);
      setEditUser(p => ({...p, [userId]: undefined}));
      loadUsers();
    } catch(e) { alert('Error updating user'); }
  };

  const handleCommissionChange = async (userId, rate) => {
    try {
      await api.put(`/api/admin/agents/${userId}/commission`, { commission_rate: parseFloat(rate) });
    } catch(e) { alert('Error updating commission rate'); }
  };

  const handleRoleChange = async (id, newRole) => {
    try { await api.put(`/api/auth/users/${id}`, { role: newRole }); loadUsers(); }
    catch (err) { alert('Error updating role'); }
  };

  const handleAgentTypeChange = async (id, newType) => {
    try {
      await api.put(`/api/admin/agents/${id}/type`, { role: newType });
      loadUsers();
    } catch (err) { alert('Error updating agent type'); }
  };

  if (!isAdmin) return <div className="glass rounded-xl p-8 text-center"><Shield className="w-12 h-12 text-dark-500 mx-auto mb-3" /><p className="text-dark-400">Admin access required</p></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div><h3 className="text-xl font-bold text-white">Team ({users.length})</h3><p className="text-dark-400 text-sm">Manage executives, managers, and HR</p></div>
        <div className="flex gap-2">
          <label className="flex items-center gap-2 text-dark-400 text-sm"><input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} />Show Archived</label>
          <button onClick={() => { setShowForm(true); setGeneratedPw(''); }} className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-dark-900 font-medium rounded-lg text-sm"><Plus className="w-4 h-4" />Add User</button>
        </div>
      </div>

      {loading ? <div className="flex justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div></div> :
      users.length === 0 ? <div className="glass rounded-xl p-8 text-center"><Users className="w-12 h-12 text-dark-500 mx-auto mb-3" /><p className="text-dark-400">No team members yet</p></div> :
      <div className="space-y-2">
        {users.map(u => (
          <div key={u.id} className={`glass rounded-xl p-4 ${u.is_deleted ? 'opacity-60' : ''}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${u.is_active ? 'bg-primary-500/20' : 'bg-red-500/20'}`}>
                  <span className={`font-bold ${u.is_active ? 'text-primary-400' : 'text-red-400'}`}>{u.name?.charAt(0)?.toUpperCase()}</span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-white font-medium">{u.name}</h4>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      u.role === 'manager' ? 'bg-purple-500/10 text-purple-400' :
                      u.role === 'hr' ? 'bg-pink-500/10 text-pink-400' :
                      u.role === 'admin' ? 'bg-red-500/10 text-red-400' :
                      'bg-blue-500/10 text-blue-400'
                    }`}>{u.role === 'voip_agent' ? 'executive' : u.role}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${u.agent_type === 'voip_agent' ? 'bg-green-500/10 text-green-400' : 'bg-dark-600 text-dark-400'}`}>
                      {u.agent_type === 'voip_agent' ? '📞 VoIP' : '📱 Normal'}
                    </span>
                    {!u.is_active && <span className="px-2 py-0.5 rounded text-xs bg-red-500/10 text-red-400">Inactive</span>}
                    {u.is_deleted && <span className="px-2 py-0.5 rounded text-xs bg-dark-500/10 text-dark-400">Archived</span>}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-dark-400 mt-1">
                    {editUser[u.id] ? (
                      <div className="flex flex-wrap gap-2" onClick={e => e.stopPropagation()}>
                        <input placeholder="Name" defaultValue={u.name} onChange={e => setEditUser(p => ({...p, [u.id]: {...(p[u.id]||{}), name: e.target.value}}))} className="px-2 py-1 bg-dark-700 rounded text-white text-xs w-28" />
                        <input placeholder="Username" defaultValue={u.username} onChange={e => setEditUser(p => ({...p, [u.id]: {...(p[u.id]||{}), username: e.target.value}}))} className="px-2 py-1 bg-dark-700 rounded text-white text-xs w-28" />
                        <input placeholder="Phone" defaultValue={u.phone} onChange={e => setEditUser(p => ({...p, [u.id]: {...(p[u.id]||{}), phone: e.target.value}}))} className="px-2 py-1 bg-dark-700 rounded text-white text-xs w-36" />
                        <input placeholder="Email" defaultValue={u.email} onChange={e => setEditUser(p => ({...p, [u.id]: {...(p[u.id]||{}), email: e.target.value}}))} className="px-2 py-1 bg-dark-700 rounded text-white text-xs w-40" />
                        <button onClick={() => handleUserUpdate(u.id)} className="px-2 py-1 bg-green-500/10 text-green-400 rounded text-xs">Save</button>
                        <button onClick={() => setEditUser(p => ({...p, [u.id]: undefined}))} className="px-2 py-1 bg-dark-700 text-dark-400 rounded text-xs">Cancel</button>
                      </div>
                    ) : (
                      <>
                        <span>@{u.username}</span>
                        {u.email && <span>{u.email}</span>}
                        {u.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{u.phone}</span>}
                    {u.joining_date && <span>Joined: {u.joining_date}</span>}
                    {u.twilio_number && <span className="text-green-400 flex items-center gap-1"><PhoneCall className="w-3 h-3" />{u.twilio_number}</span>}
                    <span className="text-primary-400">Commission: {u.commission_rate || 1}%</span>
                    {u.agent_type === 'voip_agent' && u.twilio_number && <span className="text-green-400 text-xs">🔗 {u.twilio_number}</span>}
                    {u.agent_type === 'voip_agent' && !u.twilio_number && <span className="text-orange-400 text-xs">⚠ No Twilio number</span>}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select value={u.role === 'voip_agent' ? 'executive' : u.role} onChange={e => handleRoleChange(u.id, e.target.value)} className="px-2 py-1 bg-dark-800 border border-dark-600 rounded text-white text-xs">
                  <option value="executive">Executive</option><option value="manager">Manager</option><option value="hr">HR</option><option value="marketing">Marketing</option><option value="toplevman">TopLevMan</option>
                </select>
                <select value={u.agent_type || 'normal_agent'} onChange={e => handleAgentTypeChange(u.id, e.target.value)} className="px-2 py-1 bg-dark-800 border border-dark-600 rounded text-white text-xs">
                  <option value="normal_agent">📱 Normal</option><option value="voip_agent">📞 VoIP</option>
                </select>
                <div className="flex items-center gap-1" title="Commission %">
                  <input
                    type="number"
                    defaultValue={u.commission_rate || 1}
                    step="0.1"
                    min="0"
                    max="100"
                    onBlur={e => handleCommissionChange(u.id, e.target.value)}
                    className="w-16 px-2 py-1 bg-dark-800 border border-dark-600 rounded text-white text-xs"
                    title="Commission % — press Tab or click away to save"
                  />
                  <span className="text-dark-400 text-xs">%</span>
                </div>
                <button onClick={() => setEditUser(p => ({...p, [u.id]: {}}))} className="p-1.5 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 rounded" title="Edit Details"><Edit2 className="w-3.5 h-3.5" /></button>
                <button onClick={() => setResetPw({ userId: u.id, newPassword: '' })} className="p-1.5 bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 rounded" title="Reset Password"><Key className="w-3.5 h-3.5" /></button>
                {u.is_deleted ? (
                  <button onClick={() => handleRestore(u.id)} className="p-1.5 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded" title="Restore"><RotateCcw className="w-3.5 h-3.5" /></button>
                ) : (
                  <>
                    {u.is_active ? <button onClick={() => handleDeactivate(u.id)} className="p-1.5 bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 rounded" title="Deactivate"><UserX className="w-3.5 h-3.5" /></button> :
                    <button onClick={() => handleActivate(u.id)} className="p-1.5 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded" title="Activate"><UserCheck className="w-3.5 h-3.5" /></button>}
                    <button onClick={() => handleSoftDelete(u.id)} className="p-1.5 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded" title="Archive"><X className="w-3.5 h-3.5" /></button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>}

      {/* Register Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-dark-900 border border-dark-700 rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-dark-700">
              <h3 className="text-lg font-semibold text-white">Add Team Member</h3>
              <button onClick={() => { setShowForm(false); setGeneratedPw(''); }} className="p-1 text-dark-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {generatedPw ? (
              <div className="p-5 space-y-4">
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-green-400 font-medium mb-1">User Created Successfully!</p>
                  <p className="text-dark-300 text-sm mb-2">Share these credentials securely with the team member:</p>
                  <div className="flex items-center gap-2 p-2 bg-dark-800 rounded">
                    <code className="text-white text-sm flex-1">Password: {generatedPw}</code>
                    <button onClick={() => { navigator.clipboard.writeText(generatedPw); alert('Copied!'); }} className="p-1 text-primary-400 hover:bg-primary-500/10 rounded"><Copy className="w-4 h-4" /></button>
                  </div>
                </div>
                <button onClick={() => { setShowForm(false); setGeneratedPw(''); }} className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-dark-900 rounded-lg text-sm font-medium">Done</button>
              </div>
            ) : (
              <form onSubmit={handleRegister} className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm text-dark-300 mb-1">Username *</label><input type="text" value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})} required className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm" /></div>
                  <div><label className="block text-sm text-dark-300 mb-1">Password</label><input type="text" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} placeholder="Auto-generate" className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm" /></div>
                </div>
                <div><label className="block text-sm text-dark-300 mb-1">Full Name *</label><input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm text-dark-300 mb-1">Email</label><input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm" /></div>
                  <div><label className="block text-sm text-dark-300 mb-1">Phone</label><input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="+919876543210" className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm" /></div>
                </div>
                <div><label className="block text-sm text-dark-300 mb-1">Role</label><select value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm"><option value="executive">Executive</option><option value="manager">Manager</option><option value="hr">HR</option><option value="marketing">Marketing</option><option value="toplevman">TopLevMan</option></select></div>
                <p className="text-dark-500 text-xs">If password is left blank, it will be auto-generated.</p>
                <div className="flex gap-3"><button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 bg-dark-800 text-dark-300 rounded-lg text-sm">Cancel</button><button type="submit" className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-dark-900 rounded-lg text-sm font-medium">Create User</button></div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPw.userId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-dark-900 border border-dark-700 rounded-2xl w-full max-w-sm p-5">
            <h3 className="text-lg font-semibold text-white mb-4">Reset Password</h3>
            <input type="text" value={resetPw.newPassword} onChange={e => setResetPw({...resetPw, newPassword: e.target.value})} placeholder="New password" className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm mb-4" />
            <div className="flex gap-3"><button onClick={() => setResetPw({ userId: null, newPassword: '' })} className="flex-1 py-2 bg-dark-800 text-dark-300 rounded-lg text-sm">Cancel</button><button onClick={handleResetPassword} className="flex-1 py-2 bg-primary-500 hover:bg-primary-600 text-dark-900 rounded-lg text-sm font-medium">Reset</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
